// Hook: resolve a Blob Storage path into a short-lived read SAS URL.
//
// Lesson steps store the canonical blob path (`<schoolId>/<lessonId>/...`)
// but the browser can't fetch a blob without a SAS query string. This
// hook calls the backend's /files/read-url endpoint on mount and
// returns a fresh URL the browser can fetch directly.
//
// Cached in a module-level Map so multiple components viewing the
// same file (admin + cover + teacher previews all on screen at once)
// share a single round-trip per session. Entries auto-evict when the
// SAS expires.

import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { getFileReadUrl } from './api';

interface CacheEntry {
  url: string;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string>>();

/** Resolve `blobPath` to a usable SAS URL. Returns `null` while loading
 *  or if resolution failed. Friendly filename is forwarded so the
 *  download/preview shows the original name rather than the UID-prefixed
 *  blob path. */
export function useBlobReadUrl(
  blobPath: string | undefined,
  fileName: string | undefined,
): { url: string | null; loading: boolean; error: string | null } {
  const [url, setUrl] = useState<string | null>(() =>
    blobPath ? cachedUrl(blobPath) : null,
  );
  const [loading, setLoading] = useState<boolean>(() =>
    !!(blobPath && !cachedUrl(blobPath)),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!blobPath) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }
    const cached = cachedUrl(blobPath);
    if (cached) {
      setUrl(cached);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    resolveReadUrl(blobPath, fileName).then(
      (resolved) => {
        if (cancelled) return;
        setUrl(resolved);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      },
    );
    return () => { cancelled = true; };
  }, [blobPath, fileName]);

  return { url, loading, error };
}

function cachedUrl(blobPath: string): string | null {
  const entry = cache.get(blobPath);
  if (!entry) return null;
  // Treat anything within 60s of expiry as expired so an in-flight
  // fetch doesn't burn the URL right as it goes stale.
  if (entry.expiresAt < Date.now() + 60_000) {
    cache.delete(blobPath);
    return null;
  }
  return entry.url;
}

async function resolveReadUrl(blobPath: string, fileName: string | undefined): Promise<string> {
  // De-duplicate concurrent resolutions for the same blobPath — common
  // when a list of items mounts at once.
  const existing = inflight.get(blobPath);
  if (existing) return existing;
  const promise = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error('Not signed in');
    const { readUrl, expiresAt } = await getFileReadUrl(token, { blobPath, fileName });
    cache.set(blobPath, { url: readUrl, expiresAt });
    return readUrl;
  })();
  inflight.set(blobPath, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(blobPath);
  }
}
